import { PerfRun, PerfRunStatus } from '@/services/perfTestsApi';

interface Props {
  runs: PerfRun[];
  onRowClick?: (run: PerfRun) => void;
  selectedRunId?: string | null;
}

const STATUS_COLORS: Record<PerfRunStatus, string> = {
  pending: 'bg-gray-100 text-gray-700',
  running: 'bg-indigo-100 text-indigo-700 animate-pulse',
  passed: 'bg-emerald-100 text-emerald-700',
  failed: 'bg-amber-100 text-amber-700',
  cancelled: 'bg-gray-100 text-gray-700',
  error: 'bg-red-100 text-red-700',
};

function fmtMs(ms: number | string | null): string {
  if (ms === null || ms === undefined) return '—';
  const n = Number(ms);
  if (!Number.isFinite(n)) return '—';
  if (n < 1) return n.toFixed(2) + ' ms';
  if (n < 1000) return n.toFixed(0) + ' ms';
  return (n / 1000).toFixed(2) + ' s';
}

function fmtDuration(ms: number | null): string {
  if (!ms) return '—';
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  return `${Math.floor(ms / 60_000)} min ${Math.floor((ms % 60_000) / 1000)} s`;
}

function fmtRate(rate: number | string | null): string {
  if (rate === null || rate === undefined) return '—';
  const n = Number(rate);
  if (!Number.isFinite(n)) return '—';
  return (n * 100).toFixed(2) + ' %';
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function RunListTable({ runs, onRowClick, selectedRunId }: Props) {
  if (runs.length === 0) {
    return (
      <div className="text-sm text-gray-500 text-center py-8 border border-dashed border-gray-300 rounded-md">
        Aucun run pour l'instant. Lance ton premier test ci-dessus.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto border border-gray-200 rounded-md">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-xs text-gray-600 uppercase">
          <tr>
            <th className="px-3 py-2 text-left">Démarré</th>
            <th className="px-3 py-2 text-left">Scénario</th>
            <th className="px-3 py-2 text-left">Profil</th>
            <th className="px-3 py-2 text-left">Status</th>
            <th className="px-3 py-2 text-right">Durée</th>
            <th className="px-3 py-2 text-right">p95 CRUD</th>
            <th className="px-3 py-2 text-right">p95 Aggreg</th>
            <th className="px-3 py-2 text-right">Erreurs</th>
            <th className="px-3 py-2 text-right">Reqs</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((r) => {
            const selected = selectedRunId === r.id;
            return (
              <tr
                key={r.id}
                onClick={() => onRowClick?.(r)}
                className={`border-t border-gray-100 cursor-pointer ${
                  selected ? 'bg-indigo-50' : 'hover:bg-gray-50'
                }`}
              >
                <td className="px-3 py-2 text-gray-700">{fmtDate(r.started_at)}</td>
                <td className="px-3 py-2 font-medium text-gray-900">{r.scenario_name}</td>
                <td className="px-3 py-2 text-gray-700">{r.profile}</td>
                <td className="px-3 py-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[r.status]}`}>
                    {r.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtDuration(r.duration_ms)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtMs(r.p95_crud_ms)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtMs(r.p95_aggregation_ms)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtRate(r.http_req_failed_rate)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.total_requests ?? '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
